import { AccessoriesCategoryId, CategoryIds, SinglePageEnum } from "./enums";

const GLB_EXTENSIONS = ["glb", "gltf"];

const ATTRIBUTE_VALUE_ORDER = ["Size", "House Model", "Article Code"];

const MULTIPLE_COMPONENT_OVERRIDES = new Set([
  "125:Sliding Windows",
  "125:Tillval Type 6",
  "126:Sliding Windows",
  "126:Tillval Type 6",
  "127:Tillval Type 6",
  "128:Floor Pdf",
  "128:Gable Type",
  "128:Pivot Type",
  "128:Tillval Type 6",
  "129:Electricity",
  "129:Furniture Types 3",
  "129:Heating_electricity",
]);

const REQUIRED_CHOICE_COMPONENT_OVERRIDES = new Set([
  "126:Roof Entrance",
]);

const CATEGORY_NAME_TO_ID = {
  [SinglePageEnum.Accessories]: AccessoriesCategoryId,
};
CategoryIds.houses.forEach((category) => {
  CATEGORY_NAME_TO_ID[category.name] = category.id;
});

function getAssetType(assetUrl) {
  const extension = assetUrl.split(".").pop()?.toLowerCase();
  return GLB_EXTENSIONS.includes(extension) ? "glb" : "png";
}

function normalizeAttributeValues(attributeValues) {
  return Object.keys(attributeValues)
    .sort((a, b) => {
      const orderA = ATTRIBUTE_VALUE_ORDER.indexOf(a);
      const orderB = ATTRIBUTE_VALUE_ORDER.indexOf(b);
      if (orderA === -1 && orderB === -1) {
        return a.localeCompare(b);
      }
      if (orderA === -1) {
        return 1;
      }
      if (orderB === -1) {
        return -1;
      }
      return orderA - orderB;
    })
    .map((name) => ({ value: attributeValues[name] }));
}

function normalizeAsset(asset) {
  return {
    asset_url: asset.assetUrl,
    preview_image_url: asset.previewImageUrl,
    asset_type: getAssetType(asset.assetUrl),
    attribute_values: normalizeAttributeValues(asset.attributeValues ?? {}),
  };
}

function buildVariationIndex(componentGroups) {
  const index = {};
  componentGroups.forEach((group) => {
    group.components.forEach((component) => {
      component.variations.forEach((variation) => {
        index[`${group.key}::${component.key}::${variation.key}`] = variation.id;
      });
    });
  });
  return index;
}

function normalizeDependencies(dependencies, variationIndex) {
  return dependencies
    .map((dependency) => variationIndex[`${dependency.group}::${dependency.component}::${dependency.variation}`])
    .filter((depends_on_variation_id) => depends_on_variation_id !== undefined)
    .map((depends_on_variation_id) => ({ depends_on_variation_id }));
}

function normalizeVariation(variation, componentId, variationIndex) {
  return {
    id: variation.id,
    component_id: componentId,
    key: variation.name,
    original_key: variation.key,
    price: variation.price,
    is_default: Boolean(variation.isDefault),
    assets: variation.assets.map(normalizeAsset),
    dependencies: normalizeDependencies(variation.dependencies ?? [], variationIndex),
  };
}

function normalizeComponent(component, groupId, productId, variationIndex) {
  const overrideKey = `${productId}:${component.name}`;
  return {
    id: component.id,
    name: component.name,
    group_id: groupId,
    layer_order: component.order,
    required_choice: REQUIRED_CHOICE_COMPONENT_OVERRIDES.has(overrideKey),
    multiple: MULTIPLE_COMPONENT_OVERRIDES.has(overrideKey),
    available_variations: component.variations.map((variation) =>
      normalizeVariation(variation, component.id, variationIndex)
    ),
  };
}

function normalizeComponentGroup(group, productId, variationIndex) {
  return {
    id: group.id,
    name: group.key,
    components: group.components
      .filter((component) => component.name.trim().length > 0)
      .map((component) => normalizeComponent(component, group.id, productId, variationIndex)),
  };
}

function normalizeProduct(product) {
  const variationIndex = buildVariationIndex(product.componentGroups);
  return {
    id: product.id,
    name: product.name,
    category_id: CATEGORY_NAME_TO_ID[product.category],
    preview_image: product.assets?.preview_image?.[0]?.asset ?? null,
    product_info: { description: product.description },
    component_groups: product.componentGroups.map((group) =>
      normalizeComponentGroup(group, product.id, variationIndex)
    ),
  };
}

export function normalizeProductsData(products) {
  return products.map(normalizeProduct);
}
